import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./LegalPages.css";

function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-header">
        <Button 
          variant="outline" 
          onClick={() => navigate('/')}
          className="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>
      </div>

      <div className="legal-content">
        <h1>STEP AND SIT - PRIVACY POLICY</h1>
        
        <div className="legal-meta">
          <p><strong>Effective Date:</strong> 01 March 2026</p>
          <p><strong>Last Updated:</strong> 01 March 2026</p>
        </div>

        <p className="legal-issuer">
          Issued by: <strong>Step and Sit LLP</strong><br />
          Email: <a href="mailto:admin@stepandsit.com">admin@stepandsit.com</a>
        </p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Step and Sit LLP ("Step and Sit", "we", "our", "us") respects your privacy and is committed to protecting personal data collected through the Step and Sit mobile application, website, and related services ("Platform").
          </p>
          <p>This Privacy Policy applies to:</p>
          <ul>
            <li>End Customers / Users</li>
            <li>Salon / Grooming Centers ("Service Providers")</li>
            <li>Individuals interacting with the Platform</li>
          </ul>
          <p>
            Under applicable laws including the <strong>Digital Personal Data Protection Act, 2023</strong>, Step and Sit acts as a <strong>Data Fiduciary</strong> for personal data processed through the Platform.
          </p>
          <p>By accessing or using the Platform, you consent to this Privacy Policy.</p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <p>We collect only information necessary for operating the Platform.</p>
          
          <h3>A. User Information</h3>
          <h4>Personal Information</h4>
          <ul>
            <li>Full name</li>
            <li>Phone number</li>
            <li>Email address</li>
            <li>Profile details (if voluntarily provided)</li>
            <li>Gender (optional)</li>
            <li>Date of birth (optional)</li>
          </ul>

          <h4>Booking Information</h4>
          <ul>
            <li>Selected service provider</li>
            <li>Service details</li>
            <li>Appointment date and time</li>
            <li>Booking history</li>
          </ul>

          <h4>Device & Technical Information</h4>
          <ul>
            <li>Device type and model</li>
            <li>Operating system</li>
            <li>Application version</li>
            <li>IP address</li>
            <li>Log information</li>
          </ul>

          <h4>Location Information</h4>
          <ul>
            <li>Approximate location (only with user permission)</li>
          </ul>

          <h4>Customer Support Information</h4>
          <ul>
            <li>Feedback</li>
            <li>Complaints</li>
            <li>Support communications</li>
          </ul>

          <p>Step and Sit does <strong>not</strong> collect:</p>
          <ul>
            <li>Credit or debit card details</li>
            <li>UPI credentials</li>
            <li>Banking passwords</li>
            <li>Financial authentication information</li>
          </ul>
          <p>Payments are made directly to Service Providers.</p>

          <h3>B. Service Provider Information</h3>
          <h4>Business Information</h4>
          <ul>
            <li>Salon or business name</li>
            <li>Business address</li>
            <li>GST number (if applicable)</li>
            <li>Trade license or registration details</li>
            <li>Business verification information</li>
          </ul>

          <h4>Owner / Authorized Representative Information</h4>
          <ul>
            <li>Name</li>
            <li>Phone number</li>
            <li>Email address</li>
            <li>Identity verification documents (where required)</li>
          </ul>

          <h4>Service & Product Information</h4>
          <ul>
            <li>Service listings</li>
            <li>Pricing details</li>
            <li>Availability schedules</li>
            <li>Offers and promotions</li>
            <li>Images and descriptions</li>
            <li>Product information voluntarily provided</li>
          </ul>

          <h4>Operational Information</h4>
          <ul>
            <li>Booking records</li>
            <li>Appointment history</li>
            <li>Cancellation data</li>
            <li>Platform performance information</li>
          </ul>

          <h4>Settlement Information</h4>
          <ul>
            <li>Bank account details for platform-related settlements, where applicable</li>
          </ul>
        </section>

        <section>
          <h2>3. Purpose of Data Processing</h2>
          <p>Personal data is processed for:</p>
          <ul>
            <li>Account registration and management</li>
            <li>Booking facilitation</li>
            <li>Salon onboarding and verification</li>
            <li>Sharing booking information with selected Service Providers</li>
            <li>Customer support</li>
            <li>Platform communication</li>
            <li>Fraud prevention and misuse detection</li>
            <li>Service improvement and analytics</li>
            <li>Compliance with legal obligations</li>
          </ul>
          <p>Step and Sit does not conduct automated profiling or automated decision-making.</p>
        </section>

        <section>
          <h2>4. Legal Basis for Processing</h2>
          <p>Personal data is processed based on:</p>
          <ul>
            <li>Consent provided by Users or Service Providers</li>
            <li>Performance of contractual obligations</li>
            <li>Compliance with applicable laws</li>
            <li>Legitimate business interests such as platform security and improvement</li>
          </ul>
          <p>Consent may be withdrawn at any time subject to operational limitations.</p>
        </section>

        <section>
          <h2>5. Consent</h2>
          <p>Consent is obtained through:</p>
          <ul>
            <li>Account registration</li>
            <li>Platform onboarding</li>
            <li>Acceptance of platform terms</li>
            <li>Permission-based device access</li>
          </ul>
          <p>Withdrawal of consent may limit Platform functionality.</p>
        </section>

        <section>
          <h2>6. Information Sharing</h2>
          <p>Personal data may be shared only as necessary with:</p>
          <ul>
            <li>Selected Service Providers for booking fulfillment</li>
            <li>Authorized technology and infrastructure service providers</li>
            <li>Communication service vendors</li>
            <li>Analytics and technical support providers</li>
            <li>Government authorities or regulators where legally required</li>
          </ul>
          <p>Step and Sit does <strong>not sell, rent, or trade personal data</strong>.</p>
        </section>

        <section>
          <h2>7. Data Storage & Hosting</h2>
          <p>
            Personal data is stored and processed on secure cloud infrastructure maintained by authorized third-party hosting providers.
          </p>
          <p>All data storage servers used for Platform operations are located within India.</p>
          <p>Such service providers process data only under contractual confidentiality and security obligations.</p>
          <p>
            Step and Sit does not intentionally transfer personal data outside India. Any future transfer shall comply with applicable laws including the Digital Personal Data Protection Act, 2023.
          </p>
        </section>

        <section>
          <h2>8. Use of Third-Party Service Providers</h2>
          <p>Step and Sit may engage third-party service providers for:</p>
          <ul>
            <li>Cloud hosting and infrastructure management</li>
            <li>Communication services</li>
            <li>Analytics and performance monitoring</li>
            <li>Technical maintenance and support</li>
          </ul>
          <p>These providers process data only on instructions from Step and Sit and do not obtain ownership rights over personal data.</p>
          <p>Step and Sit remains responsible as the Data Fiduciary.</p>
        </section>

        <section>
          <h2>9. Data Security</h2>
          <p>We implement reasonable safeguards including:</p>
          <ul>
            <li>Encrypted data transmission (HTTPS)</li>
            <li>Secure server infrastructure</li>
            <li>Access control mechanisms</li>
            <li>Monitoring and logging systems</li>
          </ul>
          <p>No digital system can guarantee absolute security.</p>
        </section>

        <section>
          <h2>10. Data Retention</h2>
          <p>Personal data is retained:</p>
          <ul>
            <li>While accounts remain active</li>
            <li>For legal and regulatory compliance</li>
            <li>For dispute resolution</li>
            <li>For fraud prevention</li>
          </ul>
          <p>Data may be archived where required by law even after account closure.</p>
        </section>

        <section>
          <h2>11. Rights of Data Principals</h2>
          <p>Under applicable law including the DPDP Act, individuals have the right to:</p>
          <ul>
            <li>Access personal data</li>
            <li>Request correction or updates</li>
            <li>Request deletion of personal data</li>
            <li>Withdraw consent</li>
            <li>Lodge grievances</li>
            <li>Nominate another individual to exercise rights in case of death or incapacity</li>
          </ul>
          <p>
            Requests may be submitted to: <a href="mailto:admin@stepandsit.com">admin@stepandsit.com</a>
          </p>
          <p>
            To request account deletion, you may use our{' '}
            <a href="/delete-account" style={{ fontWeight: 600 }}>Account Deletion Request Form</a>.
          </p>
        </section>

        <section>
          <h2>12. Children's Privacy</h2>
          <p>The Platform is not intended for individuals below 18 years of age.</p>
          <p>We do not knowingly collect personal data from minors. Such data will be deleted upon identification.</p>
        </section>

        <section>
          <h2>13. Third-Party Links and Services</h2>
          <p>The Platform may contain links to third-party services.</p>
          <p>
            Step and Sit is not responsible for external privacy practices or third-party systems used independently by Service Providers.
          </p>
        </section>

        <section>
          <h2>14. Service & Product Responsibility</h2>
          <p>
            All services and products available through the Platform are provided independently by Service Providers.
          </p>
          <p>
            Step and Sit acts solely as a booking facilitator and is not responsible for service quality, treatment outcomes, or product-related issues.
          </p>
        </section>

        <section>
          <h2>15. Data Breach Response</h2>
          <p>
            In the event of a personal data breach, Step and Sit will take appropriate remedial measures and notify affected parties or authorities where required under applicable law.
          </p>
        </section>

        <section>
          <h2>16. Termination & Post-Exit Handling</h2>
          <p>Upon account closure or termination:</p>
          <ul>
            <li>Public listings may be removed</li>
            <li>Future bookings disabled</li>
            <li>Certain records retained for legal or compliance purposes</li>
          </ul>
        </section>

        <section>
          <h2>17. Policy Updates</h2>
          <p>This Privacy Policy may be updated periodically.</p>
          <p>
            Updated versions will be published on the Platform with a revised effective date. Continued use constitutes acceptance of the updated Policy.
          </p>
        </section>

        <section>
          <h2>18. Grievance Officer</h2>
          <p>In accordance with applicable Indian laws and IT Rules:</p>
          <p>
            <strong>Grievance Officer</strong><br />
            Step and Sit LLP
          </p>
          <p>Email: <a href="mailto:admin@stepandsit.com">admin@stepandsit.com</a></p>
          <ul>
            <li>Grievances will be acknowledged within 48 hours</li>
            <li>Resolution will be attempted within 15 days where feasible</li>
          </ul>
        </section>

        <section>
          <h2>19. Contact Information</h2>
          <p>
            <strong>Step and Sit LLP</strong><br />
            Email: <a href="mailto:admin@stepandsit.com">admin@stepandsit.com</a><br />
            Website: <a href="https://www.stepandsit.com" target="_blank" rel="noopener noreferrer">www.stepandsit.com</a>
          </p>
        </section>
      </div>

      <div className="legal-footer">
        <Button onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
